import { css } from "styled-components/macro";
import { useState, useCallback } from "react";

import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import { useSettings } from "../../contexts/SettingsManager/SettingsManager";
import {
  SettingsStyles,
  SectionStyles,
  LabelWithSubtitleStyles,
} from "./SettingsStyles";
import {
  InputLabel,
  Input,
  Tooltip,
  Button,
  FormControl,
  Checkbox,
} from "@mui/material";
import { LANGUAGES } from "../../constants/constants";
import { ONE_MINUTE_OF_MS, ONE_HOUR_OF_MS } from "../../utils/timeUtils";
import { alertColor } from "../../constants/colors";
import { ListItemButton } from "components/Nav/Drawers/drawerUtils";
import ProcessTilesConfigCollapse from "pages/DashboardPages/ProcessTilesControls/ProcessTilesExpandedTilesConfig";
import {
  isTimezoneManuallyOverriddenAtom,
  TimeZoneController,
} from "./TimeZoneController";
import { DayStartControls } from "./DayStartControls";
import { ListItemToggle } from "./ListItemToggle";
import { useIsAdmin } from "utils/customHooks/genericHooks";

// Atom to handle admin mode override
export const disableAdminModeAtom = atomWithStorage<boolean>(
  "disableAdminMode",
  false
);
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { CustomAskrEndpointListItem } from "./CustomAskrEndpointListItem";
import { useIsDarkMode } from "store/atoms";
import { atomWithStorage } from "jotai/utils";
import { AUTO_NEXT_SHIFT_DELAY } from "constants/intervals";
import { SPACE_BETWEEN } from "constants/constants";
import { useTimelinesStore } from "components/Dashboard/Timeline/TimelineCardControls/timelinesStore";
import { useTrendChartStore } from "components/Dashboard/TrendChart/TrendChartCardControls/trendChartStore";
import { useMetrics } from "components/Dashboard/GroupMetrics/useMetrics";
import {
  HiddenMetricsType,
  useHiddenMetricsByComponent,
  useVisibleMetrics,
} from "components/Dashboard/GroupMetrics/useVisibleMetrics";
import { startCase } from "lodash";
import { DataDumpAggregateType } from "types/types";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { OpenInNew, Settings } from "@mui/icons-material";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useAtom } from "jotai";

const SUBTITLE_WITH_WARNING_STYLES = {
  marginTop: "0.5em",
  lineHeight: "1.5em",
};

// tslint:disable-next-line: no-big-function
const SettingsPage = () => {
  const {
    isTourEnabled,
    setIsTourEnabled,
    languagePreference,
    setLanguagePreference,
    isPnlBreakdownChartVisible,
    setIsTrendChartVisible,
    isTrendChartVisible,
    setIsTrendChartVisibleHubletPage,
    isTrendChartVisibleHubletPage,
    setIsPnlBreakdownChartVisible,
    isLargeTiles,
    setIsLargeTiles,
    areProcessTilesExpanded,
    setAreProcessTilesExpanded,
    preferredMetric,
    setPreferredMetric,
    isTilesPageFullDay,
    setIsTilesPageFullDay,
    isAutoRefreshEnabled,
    setIsAutoRefreshEnabled,
    shouldAutoUpdate,
    setShouldAutoUpdate,
    rateRollingTimeWindow,
    setRateRollingTimeWindow,
    shouldFilterBySelectedProcesses,
    setShouldFilterBySelectedProcesses,
    setIsUsingRateRollingTimeWindow,
    isUsingRateRollingTimeWindow,
    setIsHubletPageCompactLayoutDisabled,
    isHubletPageCompactLayoutDisabled,
    autoRefreshTimeInterval,
    setAutoRefreshTimeInterval,
    isDefaultFullDaySelected,
    setIsDefaultFullDaySelected,
    shouldStayOnCurrentShift,
    setShouldStayOnCurrentShift,
    resetAllSettingsToDefaults,
    shouldAutoSwitchToCustomerTimezone,
    setShouldAutoSwitchToCustomerTimezone,
  } = useSettings();
  const [, setIsTimezoneManuallyOverridden] = useAtom(
    isTimezoneManuallyOverriddenAtom
  );
  const {
    showTimelineTooltipOnHover,
    setShowTimelineTooltipOnHover,
    showSkuDescriptionInsteadOfId,
    setShowSkuDescriptionInsteadOfId,
  } = useTimelinesStore();
  const {
    shouldAlwaysStartTrendChartBarsOnHour,
    setShouldAlwaysStartTrendChartBarsOnHour,
  } = useTrendChartStore();

  const handleDropdownChange = evt => {
    if (preferredMetric !== evt.target.value) {
      setPreferredMetric(evt.target.value);
    }
  };

  const isProduction = window.location.hostname === "flight.raven.ai";
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const isAdminUser = useIsAdmin();
  const [isDarkMode, setIsDarkMode] = useIsDarkMode();
  const [disableAdminMode, setDisableAdminMode] = useAtom(disableAdminModeAtom);

  const { translate } = useFlightTranslation();
  const { metricsRaw } = useMetrics();

  const { flightMagicStringEnabled, flightAdminMode } = useFlagsWithDefaults();
  const hasConfigAccess = isAdminUser && flightMagicStringEnabled;
  const ckey = useCustomerKey();

  const [
    {
      general: { autoSetTimezoneOverride },
    },
  ] = useFlightConfig();
  return (
    <SettingsStyles $isDarkMode={isDarkMode}>
      <div className="settingsWrapper" style={{ paddingBottom: 100 }}>
        <Typography variant="h2" className="title">
          Overall
        </Typography>
        <Divider />

        <SectionStyles>
          <List className="dayStartMsOffset" dense={true}>
            {hasConfigAccess && ckey ? (
              <ListItem>
                <a
                  href={`https://portal.raven.ai/${ckey}/flightConfig`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ margin: "auto", width: "100%" }}
                >
                  <Button
                    variant="outlined"
                    style={{
                      display: "flex",
                      gap: "0.25em",
                      alignItems: "center",
                      textTransform: "none",
                      lineHeight: 0,
                    }}
                  >
                    Flight Config Editor
                    <Settings /> <OpenInNew />
                  </Button>
                </a>
              </ListItem>
            ) : null}

            <Tooltip
              title={`Determines which shifts are grouped together in days (based on their start times), & sets your 24-hour-view start time`}
            >
              <ListItem
                style={{ display: "flex", justifyContent: SPACE_BETWEEN }}
              >
                <span>Day start offset:</span>
                <ErrorBoundary small={true} id="SettingsPage_DayStartControls">
                  <DayStartControls />
                </ErrorBoundary>
              </ListItem>
            </Tooltip>
            <ListItemToggle
              className="toggleSwitch"
              checked={isTourEnabled}
              onChange={() => setIsTourEnabled(!isTourEnabled)}
              label={`Guided tour`}
            />
            <ListItemToggle
              className="toggleSwitch"
              checked={isDarkMode}
              onChange={() => setIsDarkMode(!isDarkMode)}
              label={`Dark mode`}
            />

            <ListItem
              style={{ display: "flex", justifyContent: SPACE_BETWEEN }}
            >
              <ErrorBoundary small={true} id="SettingsPage_LanguageControls">
                <TimeZoneController
                  customCss={css`
                    width: 100%;
                    justify-content: space-between;
                  `}
                />
              </ErrorBoundary>
            </ListItem>
            {autoSetTimezoneOverride ? (
              <ListItemToggle
                className="toggleSwitch"
                checked={shouldAutoSwitchToCustomerTimezone}
                onChange={() => {
                  const nextShouldAutoSwitch =
                    !shouldAutoSwitchToCustomerTimezone;
                  setShouldAutoSwitchToCustomerTimezone(nextShouldAutoSwitch);
                  if (nextShouldAutoSwitch) {
                    setIsTimezoneManuallyOverridden(false);
                  }
                }}
                label={
                  <LabelWithSubtitle
                    label={`Auto-switch to work schedule timezone`}
                    subtitle={`Whenever you switch to a different work schedule, switch to the associated timezone.`}
                  />
                }
              />
            ) : null}
            <ListItem
              style={{ display: "flex", justifyContent: SPACE_BETWEEN }}
            >
              <span>Language:</span>
              <Select
                variant="standard"
                className="dropdown"
                value={languagePreference}
                onChange={e => setLanguagePreference(String(e.target.value))}
                MenuProps={{ disableScrollLock: true }}
              >
                {Object.entries(LANGUAGES)
                  .sort((a, b) => (a[1].text > b[1].text ? 1 : -1))
                  .map(([key, lng]) => (
                    <MenuItem key={key} value={lng.value}>
                      {lng.text}
                    </MenuItem>
                  ))}
              </Select>
            </ListItem>
          </List>
        </SectionStyles>

        <Typography variant="h2" className="title">
          Tiles Page
        </Typography>
        <Divider />

        <SectionStyles>
          <List dense={true}>
            <ListItemToggle
              className="toggleSwitch"
              checked={isTilesPageFullDay}
              onChange={() => setIsTilesPageFullDay(!isTilesPageFullDay)}
              label={
                <LabelWithSubtitle
                  label={`24 hour view on tiles`}
                  subtitle={`*If you use counts, they will not be visible while in 24 hour view.`}
                />
              }
            />
            <ListItemToggle
              className="toggleSwitch"
              checked={isLargeTiles}
              onChange={() => setIsLargeTiles(!isLargeTiles)}
              label={`Large tiles`}
            />
            <ListItemToggle
              className="toggleSwitch"
              checked={areProcessTilesExpanded}
              onChange={() =>
                setAreProcessTilesExpanded(!areProcessTilesExpanded)
              }
              label={`Expanded tiles`}
            />
            <div className="expandedTilesConfig">
              <ListItemButton onClick={() => setIsConfigOpen(!isConfigOpen)}>
                Configure expanded tiles{" "}
                <ExpandMoreIcon
                  className="expandMoreIcon"
                  style={{
                    transform: `rotate(${isConfigOpen ? -180 : 0}deg)`,
                  }}
                />
              </ListItemButton>
              <ErrorBoundary small={true} id="SettingsPage_ProcessTilesConfig">
                <ProcessTilesConfigCollapse
                  open={isConfigOpen}
                  setIsConfigOpen={() => {
                    return;
                  }}
                  isSettingsPage={true}
                />
              </ErrorBoundary>
            </div>
          </List>
        </SectionStyles>

        <Typography variant="h2" className="title">
          Timeline Page
        </Typography>
        <Divider />

        <SectionStyles>
          <List dense={true}>
            <ListItemToggle
              className="toggleSwitch"
              checked={isTrendChartVisible}
              onChange={() => setIsTrendChartVisible(!isTrendChartVisible)}
              label={`Trend chart`}
            />
            <ListItemToggle
              className="toggleSwitch"
              checked={isPnlBreakdownChartVisible}
              onChange={() =>
                setIsPnlBreakdownChartVisible(!isPnlBreakdownChartVisible)
              }
              label={
                <div>
                  P<span style={{ fontFamily: "Garamond" }}>&</span>L breakdown
                  chart
                </div>
              }
            />
            <ListItemToggle
              className="toggleSwitch"
              checked={isDefaultFullDaySelected}
              onChange={() =>
                setIsDefaultFullDaySelected(!isDefaultFullDaySelected)
              }
              label={`Default to 24 hour view instead of shift view`}
            />
            <ListItemToggle
              className="toggleSwitch"
              checked={showTimelineTooltipOnHover}
              onChange={() =>
                setShowTimelineTooltipOnHover(!showTimelineTooltipOnHover)
              }
              label={`Show tooltip on hover instead of on click`}
            />
            <ListItemToggle
              className="toggleSwitch"
              checked={showSkuDescriptionInsteadOfId}
              onChange={() =>
                setShowSkuDescriptionInsteadOfId(!showSkuDescriptionInsteadOfId)
              }
              label={
                <LabelWithSubtitle
                  label="SKU Timeline - show description instead of ID"
                  subtitle="When enabled, SKU descriptions will be shown instead of SKU IDs in the timeline view"
                />
              }
            />
            <ListItemToggle
              className="toggleSwitch"
              checked={shouldStayOnCurrentShift}
              onChange={() =>
                setShouldStayOnCurrentShift(!shouldStayOnCurrentShift)
              }
              label={
                <LabelWithSubtitle
                  label={`Stay on current shift`}
                  subtitle={translate("Stay_on_current_shift_explanation", "", {
                    minutes: (AUTO_NEXT_SHIFT_DELAY / ONE_MINUTE_OF_MS).toFixed(
                      0
                    ),
                  })}
                />
              }
            />
            <ListItemToggle
              className="toggleSwitch"
              checked={shouldAlwaysStartTrendChartBarsOnHour}
              onChange={() =>
                setShouldAlwaysStartTrendChartBarsOnHour(
                  !shouldAlwaysStartTrendChartBarsOnHour
                )
              }
              label={
                <LabelWithSubtitle
                  label={translate("Start_on_hour")}
                  subtitle={translate("Start_on_hour_explanation")}
                />
              }
            />
          </List>
        </SectionStyles>

        <>
          <Typography variant="h2" className="title">
            Machine Details Page
          </Typography>
          <Divider />

          <SectionStyles>
            <List dense={true}>
              <ListItemToggle
                style={{ marginBottom: -12 }}
                className="toggleSwitch"
                checked={!isHubletPageCompactLayoutDisabled}
                onChange={() =>
                  setIsHubletPageCompactLayoutDisabled(
                    !isHubletPageCompactLayoutDisabled
                  )
                }
                label={
                  <LabelWithSubtitle
                    label={translate(`Auto_compact_layout`)}
                    subtitle={translate("Auto_compact_layout_explanation")}
                  />
                }
              />
              <ListItemToggle
                style={{ marginBottom: -12 }}
                className="toggleSwitch"
                checked={isUsingRateRollingTimeWindow}
                onChange={() =>
                  setIsUsingRateRollingTimeWindow(!isUsingRateRollingTimeWindow)
                }
                label={`Calculate rates over a specific time range (instead of over the whole shift)`}
              />
              <ListItem
                className="rateRollingTimeWindow"
                style={{
                  display: "flex",
                  justifyContent: SPACE_BETWEEN,
                  ...(isUsingRateRollingTimeWindow
                    ? {}
                    : { pointerEvents: "none", opacity: 0.5 }),
                }}
              >
                <span>
                  <LabelWithSubtitleStyles>
                    <div className="label">Maximum time range</div>
                    <div
                      style={SUBTITLE_WITH_WARNING_STYLES}
                      className="subtitle"
                    >
                      <span style={{ color: alertColor }}>Warning:</span> Time
                      range should never be less than your producing frequency.
                    </div>
                  </LabelWithSubtitleStyles>
                </span>
                <div
                  style={{
                    minWidth: 102,
                  }}
                >
                  <Input
                    id="rate-rolling-time-window"
                    type="number"
                    value={(rateRollingTimeWindow / ONE_MINUTE_OF_MS).toFixed(
                      0
                    )}
                    inputProps={{
                      step: 1,
                      max: 24 * 60, // max 24 hours of minutes
                      min: 1,
                    }}
                    onChange={event => {
                      setRateRollingTimeWindow(
                        Math.min(
                          Math.max(
                            Number(event.target.value) * ONE_MINUTE_OF_MS,
                            1 * ONE_MINUTE_OF_MS
                          ),
                          24 * ONE_HOUR_OF_MS
                        )
                      );
                    }}
                  />
                  <span>min</span>
                </div>
              </ListItem>
              <ListItemToggle
                className="toggleSwitch"
                checked={isTrendChartVisibleHubletPage}
                onChange={() =>
                  setIsTrendChartVisibleHubletPage(
                    !isTrendChartVisibleHubletPage
                  )
                }
                label={`Trend chart`}
              />
            </List>
          </SectionStyles>
        </>

        <Typography variant="h2" className="title">
          Metrics
        </Typography>
        <Divider />

        <SectionStyles>
          <List dense={true}>
            <ListItem className="metricDropdown">
              <InputLabel htmlFor="preferred-metric">
                Preferred metric
              </InputLabel>
              <Select
                variant="standard"
                className="dropdown"
                value={preferredMetric}
                onChange={handleDropdownChange}
                inputProps={{
                  id: "preferred-metric",
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                {Object.values(metricsRaw ?? {}).map(
                  ({ dataDumpKey, displayName }) => (
                    <MenuItem key={dataDumpKey} value={dataDumpKey}>
                      {displayName}
                    </MenuItem>
                  )
                )}
              </Select>
            </ListItem>
          </List>
          <Divider />
          <ErrorBoundary
            small={true}
            id="SettingsPage_MetricsVisibilityControls"
          >
            <MetricsVisibilityControls />
          </ErrorBoundary>
        </SectionStyles>

        <Typography variant="h2" className="title">
          Advanced
        </Typography>
        <Divider />

        <SectionStyles>
          <List dense={true}>
            <ListItemToggle
              className="toggleSwitch"
              checked={shouldAutoUpdate}
              onChange={() => setShouldAutoUpdate(!shouldAutoUpdate)}
              label={
                <LabelWithSubtitleStyles>
                  <div className="label">
                    Auto-update when updates become available
                  </div>
                  <div
                    style={SUBTITLE_WITH_WARNING_STYLES}
                    className="subtitle"
                  >
                    Automatically refresh the app to receive updates instead of
                    showing the {`"`}Update is available{`"`} banner.
                    <span style={{ display: "block" }}>
                      <span style={{ color: alertColor }}>Warning:</span> If
                      enabled, the app will occasionally auto-refresh and your
                      work may be interrupted.
                    </span>
                  </div>
                </LabelWithSubtitleStyles>
              }
            />
            <ListItemToggle
              className="toggleSwitch"
              checked={isAutoRefreshEnabled}
              onChange={() => setIsAutoRefreshEnabled(!isAutoRefreshEnabled)}
              style={{ marginBottom: -12 }}
              label={
                <LabelWithSubtitleStyles>
                  <div className="label">
                    Auto-refresh (for low connectivity)
                  </div>
                  <div
                    style={SUBTITLE_WITH_WARNING_STYLES}
                    className="subtitle"
                  >
                    Intended for end-of-line devices in low internet
                    connectivity areas.
                    <span style={{ display: "block" }}>
                      <span style={{ color: alertColor }}>Warning:</span> If
                      enabled, will increase data usage (not recommended for
                      mobile).
                    </span>
                  </div>
                </LabelWithSubtitleStyles>
              }
            />
            <ListItem
              className="autoRefreshTimeInterval"
              style={{
                display: "flex",
                justifyContent: SPACE_BETWEEN,
                ...(isAutoRefreshEnabled
                  ? {}
                  : { pointerEvents: "none", opacity: 0.5 }),
              }}
            >
              <div className="label">Auto-refresh time interval</div>
              <div
                style={{
                  minWidth: 102,
                }}
              >
                <Input
                  id="auto-refresh-time-interval"
                  type="number"
                  value={(autoRefreshTimeInterval / ONE_MINUTE_OF_MS).toFixed(
                    0
                  )}
                  inputProps={{
                    step: 1,
                    max: 24 * 60, // max 24 hours of minutes (one day)
                    min: 5, // min 5 minutes
                  }}
                  onChange={event => {
                    setAutoRefreshTimeInterval(
                      Math.min(
                        Math.max(
                          Number(event.target.value) * ONE_MINUTE_OF_MS,
                          5 * ONE_MINUTE_OF_MS
                        ),
                        24 * ONE_HOUR_OF_MS
                      )
                    );
                  }}
                />
                <span>min</span>
              </div>
            </ListItem>
            {!isProduction && (
              <ListItemToggle
                className="toggleSwitch"
                checked={shouldFilterBySelectedProcesses}
                onChange={() =>
                  setShouldFilterBySelectedProcesses(
                    !shouldFilterBySelectedProcesses
                  )
                }
                label={
                  <LabelWithSubtitle
                    label={`Enable filtering rollups by bottleneck processes`}
                    subtitle={`If your process depends more strongly on certain machines than others, you'll be set up to see rollup charts for those machines only - turn this off to see the original rollup for all machines.`}
                  />
                }
              />
            )}
            <CustomAskrEndpointListItem />
            {flightAdminMode && (
              <ListItemToggle
                className="toggleSwitch"
                checked={!disableAdminMode}
                onChange={() => setDisableAdminMode(!disableAdminMode)}
                label={
                  <LabelWithSubtitle
                    label={`Admin Mode`}
                    subtitle={`Toggle admin privileges for testing purposes`}
                  />
                }
              />
            )}
            {isAdminUser && (
              <ListItem>
                <Button
                  variant="outlined"
                  style={{ margin: "20px auto auto" }}
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to reset all settings to defaults?"
                      )
                    ) {
                      resetAllSettingsToDefaults();
                    }
                  }}
                  startIcon={<WarningIcon />}
                >
                  Reset all settings to defaults
                </Button>
              </ListItem>
            )}
          </List>
        </SectionStyles>
      </div>
    </SettingsStyles>
  );
};

export function LabelWithSubtitle({
  label,
  subtitle,
}: {
  label: string;
  subtitle: JSX.Element | string;
}) {
  return (
    <LabelWithSubtitleStyles>
      <div className="label">{label}</div>
      <div className="subtitle">{subtitle}</div>
    </LabelWithSubtitleStyles>
  );
}

export default SettingsPage;

function MetricsVisibilityControls() {
  const [hiddenMetricsByComponent, setHiddenMetricsByComponent] =
    useHiddenMetricsByComponent();
  const { metricsRaw } = useMetrics();
  const visibleMetrics = useVisibleMetrics();

  const toggleMetricVisibility = useCallback(
    (
      componentName: keyof HiddenMetricsType,
      dataDumpKey: keyof DataDumpAggregateType
    ) => {
      const visible =
        hiddenMetricsByComponent[componentName].includes(dataDumpKey);
      setHiddenMetricsByComponent(prev => ({
        ...prev,
        [componentName]: visible
          ? hiddenMetricsByComponent[componentName].filter(
              key => key !== dataDumpKey
            )
          : [...hiddenMetricsByComponent[componentName], dataDumpKey],
      }));
    },
    [hiddenMetricsByComponent, setHiddenMetricsByComponent]
  );

  return (
    <List className="metricsList" dense={true}>
      <ListItem>
        <div className="flex metricsListTitle">
          <div className="metricsToDisplayTitle">Metrics to display</div>
          {Object.keys(visibleMetrics).map(componentName => (
            <div className="header" key={componentName}>
              <div>{startCase(componentName)}</div>
            </div>
          ))}
        </div>
      </ListItem>
      <FormControl fullWidth={true}>
        {/* for each metric... */}
        {metricsRaw?.map(({ dataDumpKey, displayName }) => (
          <div className="metricCheckboxesRow" key={dataDumpKey}>
            {/* show the metric name */}
            <div className="metricName">{displayName}</div>
            {/* then show a checkboxes for each visibleMetrics key */}
            <div className="metricCheckboxes">
              {Object.entries(visibleMetrics).map(
                ([componentName, visibleMetricsArr]) => (
                  <Checkbox
                    key={componentName}
                    checked={
                      !hiddenMetricsByComponent[componentName].includes(
                        dataDumpKey
                      )
                    }
                    onChange={() =>
                      toggleMetricVisibility(
                        componentName as keyof HiddenMetricsType,
                        dataDumpKey as keyof DataDumpAggregateType
                      )
                    }
                  />
                )
              )}
            </div>
          </div>
        ))}
      </FormControl>
    </List>
  );
}
